import React, { useState, useEffect } from "react";
import { CaratClosedIcon } from "../controls/ThermalHeatMapSvgs";

export interface ThresholdConfig {
  id: string;
  name: string;
  maxC: number | null;
  minC: number | null;
  enabled: boolean;
}

export const DEFAULT_THRESHOLDS: ThresholdConfig[] = [
  { id: "non_operational", name: "Non-Operational",       maxC: 50,   minC: -110, enabled: false },
  { id: "turn_on",         name: "Turn On",               maxC: null, minC: null, enabled: false },
  { id: "qualification",   name: "Qualification",         maxC: 115,  minC: -105, enabled: false },
  { id: "acceptance",      name: "Acceptance",            maxC: 105,  minC: -95,  enabled: false },
  { id: "thermal_design",  name: "Thermal Design",        maxC: 33,  minC: -23,  enabled: true  },
  { id: "operating",       name: "Operating Temperature", maxC: 100,  minC: -90,  enabled: false },
];

export interface ThermalAlertThresholdsProps {
  thresholds: ThresholdConfig[];
  onChange: (updated: ThresholdConfig[]) => void;
  onApply?: (updated: ThresholdConfig[]) => void;
  defaultOpen?: boolean;
}

const ThermalAlertThresholds: React.FC<ThermalAlertThresholdsProps> = ({ thresholds, onChange, onApply, defaultOpen }) => {
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => { if (defaultOpen) setIsOpen(true); }, [defaultOpen]);

  const [drafts, setDrafts] = useState<Record<string, { max: string; min: string }>>(() =>
    Object.fromEntries(
      thresholds.map((t) => [
        t.id,
        { max: t.maxC !== null ? String(t.maxC) : "", min: t.minC !== null ? String(t.minC) : "" },
      ])
    )
  );

  const setDraftField = (id: string, field: "max" | "min", raw: string) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [field]: raw.replace(/[^-0-9.]/g, "") } }));
  };

  const isDirty = thresholds.some((t) => {
    const d = drafts[t.id];
    if (!d) return false;
    const committedMax = t.maxC !== null ? String(t.maxC) : "";
    const committedMin = t.minC !== null ? String(t.minC) : "";
    return d.max !== committedMax || d.min !== committedMin;
  });

  const applyAll = () => {
    const updated = thresholds.map((t) => {
      const d = drafts[t.id] ?? { max: "", min: "" };
      const parseField = (str: string, fallback: number | null): number | null => {
        if (str.trim() === "") return null;
        const v = parseFloat(str);
        return isNaN(v) ? fallback : v;
      };
      return { ...t, maxC: parseField(d.max, t.maxC), minC: parseField(d.min, t.minC) };
    });
    onChange(updated);
    onApply?.(updated);
    setDrafts(Object.fromEntries(
      updated.map((t) => [t.id, { max: t.maxC !== null ? String(t.maxC) : "", min: t.minC !== null ? String(t.minC) : "" }])
    ));
  };

  const toggleEnabled = (id: string) => {
    onChange(thresholds.map((x) => (x.id === id ? { ...x, enabled: !x.enabled } : x)));
  };

  return (
    <div className="thm-thresholds-panel">
      <div className="thm-thresholds-panel-header" onClick={() => setIsOpen((v) => !v)}>
        <button className="thm-thresholds-caret-btn" type="button">
          <span style={{ display: "flex", transform: isOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 300ms ease" }}>
            <CaratClosedIcon active={isOpen} />
          </span>
        </button>
        <span className="thm-thresholds-panel-title">THRESHOLDS</span>
        {isOpen && (
          <button
            className="thm-thresholds-apply-btn"
            type="button"
            disabled={!isDirty}
            onClick={(e) => { e.stopPropagation(); applyAll(); }}
          >
            Apply
          </button>
        )}
      </div>
      {isOpen && (
        <div className="thm-thresholds-list">
          {thresholds.map((t) => {
            const d = drafts[t.id] ?? { max: "", min: "" };
            return (
              <div key={t.id} className={`thm-threshold-row${t.enabled ? " thm-threshold-row--enabled" : ""}`}>
                <input
                  className="thm-threshold-row-check"
                  type="checkbox"
                  checked={t.enabled}
                  onChange={() => toggleEnabled(t.id)}
                  onClick={(e) => e.stopPropagation()}
                />
                <span className="thm-threshold-row-name">{t.name}</span>
                <div className="thm-threshold-row-input-group">
                  <span className="thm-threshold-row-input-label thm-threshold-row-input-label--max">Max</span>
                  <input
                    className="thm-threshold-row-input thm-threshold-row-input--max"
                    type="text"
                    value={d.max}
                    placeholder="—"
                    onChange={(e) => setDraftField(t.id, "max", e.target.value)}
                  />
                </div>
                <div className="thm-threshold-row-input-group">
                  <span className="thm-threshold-row-input-label thm-threshold-row-input-label--min">Min</span>
                  <input
                    className="thm-threshold-row-input thm-threshold-row-input--min"
                    type="text"
                    value={d.min}
                    placeholder="—"
                    onChange={(e) => setDraftField(t.id, "min", e.target.value)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ThermalAlertThresholds;
